/** Art-directed hero, isolated from the theme's shared slideshow implementation. */
class PreimumHeroCodex extends HTMLElement {
  #controller;
  #observer;
  #resizeObserver;
  #slides = [];
  #viewport;
  #index = 0;
  #desired = 0;
  #generation = 0;
  #timer;
  #frame;
  #animations = [];
  #progress;
  #reduced;
  #reasons = new Set();
  #failed = new Set();
  #pointer;
  #toggleWasStopped;
  #suppressClickUntil = 0;
  #busy = false;

  connectedCallback() {
    // Also works when the editor inserts an already registered element.
    this.#frame = requestAnimationFrame(() => this.#connect());
  }

  disconnectedCallback() {
    this.#generation++;
    this.#controller?.abort();
    this.#controller = undefined;
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#clearTimer();
    this.#finishAnimations();
    this.#pointer = undefined;
    this.#busy = false;
  }

  #connect() {
    if (!this.isConnected || this.#controller) return;
    this.#slides = [...this.querySelectorAll('[data-slide]')];
    this.#viewport = this.querySelector('[data-viewport]');
    if (!this.#slides.length || !this.#viewport) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#index = Math.max(0, this.#slides.findIndex((slide) => slide.getAttribute('aria-hidden') !== 'true'));
    this.#desired = this.#index;
    this.#reasons = new Set();
    this.#failed.clear();
    this.#reduced = matchMedia('(prefers-reduced-motion: reduce)');
    if (this.#reduced.matches) this.#reasons.add('reduced-motion');
    if (window.Shopify?.designMode) this.#reasons.add('editor');
    if (document.hidden) this.#reasons.add('hidden');
    if (this.matches(':hover') && matchMedia('(hover: hover)').matches) this.#reasons.add('hover');

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('click', this.#suppressSwipeClick, { capture: true, signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('focusin', () => this.#pause('focus'), { signal });
    this.addEventListener('pointerdown', (event) => {
      if (event.target.closest('[data-action="toggle"]')) {
        // Preserve the requested action before pointer focus pauses rotation.
        this.#toggleWasStopped = this.#reasons.has('manual') || this.#reasons.has('focus');
      }
    }, { signal });
    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.#pause('hover');
    }, { signal });
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') this.#resume('hover');
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      document.hidden ? this.#pause('hidden') : this.#resume('hidden');
    }, { signal });
    this.#reduced.addEventListener('change', () => {
      this.#finishAnimations();
      this.#reduced.matches ? this.#pause('reduced-motion') : this.#resume('reduced-motion');
    }, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });

    this.#viewport.addEventListener('pointerdown', this.#onPointerDown, { signal });
    window.addEventListener('pointerup', this.#onPointerUp, { signal });
    window.addEventListener('pointercancel', () => { this.#pointer = undefined; }, { signal });
    this.#viewport.addEventListener('dragstart', (event) => {
      if (this.#slides.length > 1) event.preventDefault();
    }, { signal });
    this.addEventListener('load', (event) => {
      const index = this.#slides.indexOf(event.target.closest('[data-slide]'));
      if (this.#failed.delete(index)) this.#schedule();
      this.#fitContent();
    }, { capture: true, signal });

    this.#observer = new IntersectionObserver(([entry]) => {
      entry.isIntersecting ? this.#resume('offscreen') : this.#pause('offscreen');
    }, { threshold: 0.05 });
    this.#reasons.add('offscreen');
    this.#observer.observe(this);
    let previousWidth = 0;
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (Math.abs(entry.contentRect.width - previousWidth) < 1) return;
      previousWidth = entry.contentRect.width;
      this.#finishAnimations();
      this.#fitContent();
    });
    this.#resizeObserver.observe(this.#viewport);
    document.fonts?.ready.then(() => {
      if (!signal.aborted) this.#fitContent();
    });

    this.#normalize();
    this.#fitContent();
    this.querySelector('[data-controls]')?.removeAttribute('hidden');
    this.dataset.ready = 'true';
    this.#schedule();
  }

  #normalize() {
    this.#slides.forEach((slide, index) => {
      const active = index === this.#index;
      slide.toggleAttribute('inert', !active);
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
      slide.classList.remove('is-leaving');
    });
    const count = this.querySelector('[data-count]');
    if (count) count.textContent = String(this.#index + 1).padStart(2, '0');
    this.dataset.activeSlide = String(this.#index + 1);
  }

  #fitContent() {
    const slide = this.#slides[this.#index];
    if (!slide) return;
    const content = slide.querySelector('.preimum-hero__content');
    const copy = slide.querySelector('.preimum-hero__copy');
    const media = slide.querySelector('.preimum-hero__media');
    if (!content || !copy || !media) return;
    slide.classList.remove('is-content-overflowing');
    const style = getComputedStyle(content);
    const required = copy.getBoundingClientRect().height + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    slide.classList.toggle('is-content-overflowing', required > media.getBoundingClientRect().height);
  }

  #directionTarget(step) {
    let target = (this.#desired + step + this.#slides.length) % this.#slides.length;
    while (target !== this.#desired && this.#failed.has(target)) {
      target = (target + step + this.#slides.length) % this.#slides.length;
    }
    return target;
  }

  #onClick = (event) => {
    const button = event.target.closest('button[data-action]');
    if (!button || button.closest('preimum-hero-codex') !== this) return;
    if (button.dataset.action === 'toggle') {
      const stopped = event.detail > 0 && this.#toggleWasStopped !== undefined
        ? this.#toggleWasStopped
        : this.#reasons.has('manual') || this.#reasons.has('focus');
      this.#toggleWasStopped = undefined;
      if (stopped) {
        this.#reasons.delete('manual');
        this.#reasons.delete('focus');
        this.#schedule();
      } else this.#pause('manual');
      return;
    }
    void this.#select(this.#directionTarget(button.dataset.action === 'next' ? 1 : -1), true);
  };

  #onKeydown = (event) => {
    if (this.#slides.length < 2 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    // Leave content links and form/editor controls to their native keyboard behavior.
    if (!event.target.closest('[data-controls]')) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    let target;
    if (event.key === 'ArrowRight') target = this.#directionTarget(rtl ? -1 : 1);
    if (event.key === 'ArrowLeft') target = this.#directionTarget(rtl ? 1 : -1);
    if (event.key === 'Home') target = 0;
    if (event.key === 'End') target = this.#slides.length - 1;
    if (target === undefined) return;
    event.preventDefault();
    void this.#select(target, true);
  };

  #onBlockSelect = (event) => {
    const index = this.#slides.findIndex((slide) => slide.dataset.blockId === event.detail?.blockId);
    if (index < 0) return;
    this.#pause('editor');
    void this.#select(index, false, true);
  };

  #onPointerDown = (event) => {
    if (this.#slides.length < 2 || !event.isPrimary || event.button !== 0) return;
    if (event.target.closest('.preimum-hero__copy a, button, input, textarea, select')) return;
    this.#pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now() };
  };

  #onPointerUp = (event) => {
    const start = this.#pointer;
    this.#pointer = undefined;
    if (!start || start.id !== event.pointerId) return;
    const x = event.clientX - start.x;
    const y = event.clientY - start.y;
    if (Math.abs(x) < 50 || Math.abs(x) < Math.abs(y) * 1.5 || performance.now() - start.time > 1000) return;
    this.#suppressClickUntil = performance.now() + 400;
    const rtl = getComputedStyle(this).direction === 'rtl';
    void this.#select(this.#directionTarget((x < 0) !== rtl ? 1 : -1), true);
  };

  #suppressSwipeClick = (event) => {
    if (event.detail === 0 || performance.now() > this.#suppressClickUntil || !event.target.closest('[data-viewport]')) return;
    event.preventDefault();
    event.stopPropagation();
    this.#suppressClickUntil = 0;
  };

  async #imageReady(slide) {
    const image = slide.querySelector('img');
    if (!image) return true;
    image.loading = 'eager';
    return new Promise((resolve) => {
      let settled = false;
      const finish = (ready) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        image.removeEventListener('load', decode);
        image.removeEventListener('error', failed);
        resolve(ready);
      };
      const failed = () => finish(false);
      const decode = () => {
        image.decode().then(() => finish(true), () => {
          // Source changes can reject decode; its replacement load retries it.
          if (image.complete && image.naturalWidth === 0) finish(false);
        });
      };
      const timeout = setTimeout(() => finish(image.complete && image.naturalWidth > 0), 8000);
      image.addEventListener('load', decode);
      image.addEventListener('error', failed);
      decode();
    });
  }

  async #select(index, manual = false, immediate = false) {
    if (!this.#controller || this.#slides.length < 1) return;
    if (manual) this.#pause('manual');
    const target = (index + this.#slides.length) % this.#slides.length;
    this.#desired = target;
    const generation = ++this.#generation;
    this.#clearTimer();
    this.#busy = true;
    // Let an in-flight fade finish, then prepare the latest requested image.
    await Promise.allSettled(this.#animations.map((animation) => animation.finished));
    if (generation !== this.#generation || !this.isConnected || !this.#controller) return;
    this.#finishAnimations();
    const ready = await this.#imageReady(this.#slides[target]);
    if (generation !== this.#generation || !this.isConnected || !this.#controller) return;
    if (!ready && !window.Shopify?.designMode) {
      this.#failed.add(target);
      this.#desired = this.#index;
      this.#busy = false;
      this.#schedule();
      return;
    }
    if (target === this.#index) {
      this.#busy = false;
      this.#schedule();
      return;
    }
    const outgoing = this.#slides[this.#index];
    const incoming = this.#slides[target];
    const oldHeight = this.#viewport.getBoundingClientRect().height;
    // Editor selection may replace a slide that currently owns focus.
    if (outgoing.contains(document.activeElement)) this.querySelector('[data-action="next"]')?.focus({ preventScroll: true });
    this.#index = target;
    this.#normalize();
    this.#fitContent();
    const newHeight = incoming.getBoundingClientRect().height;
    const duration = immediate || this.#reduced.matches ? 0 : Number(this.dataset.duration) || 600;
    if (duration > 0 && incoming.animate) {
      outgoing.classList.add('is-leaving');
      const options = { duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' };
      this.#animations.push(incoming.animate([{ opacity: 0 }, { opacity: 1 }], options));
      if (Math.abs(oldHeight - newHeight) > 1) {
        // Temporary interpolation only; after the fade, natural image height owns layout again.
        this.#animations.push(this.#viewport.animate([{ height: `${oldHeight}px` }, { height: `${newHeight}px` }], options));
      }
      await Promise.allSettled(this.#animations.map((animation) => animation.finished));
    }
    if (generation !== this.#generation || !this.isConnected) return;
    this.#finishAnimations();
    this.#busy = false;
    if (manual) {
      const status = this.querySelector('[data-status]');
      if (status) status.textContent = this.dataset.statusTemplate.replace('[index]', String(target + 1));
    }
    this.#schedule();
  }

  #finishAnimations() {
    this.#animations.forEach((animation) => animation.cancel());
    this.#animations = [];
    this.#slides.forEach((slide) => slide.classList.remove('is-leaving'));
  }

  #clearTimer() {
    clearTimeout(this.#timer);
    this.#timer = undefined;
    this.#progress?.cancel();
    this.#progress = undefined;
  }

  #pause(reason) { this.#reasons.add(reason); this.#schedule(); }
  #resume(reason) { this.#reasons.delete(reason); this.#schedule(); }

  #schedule() {
    this.#clearTimer();
    const toggle = this.querySelector('[data-action="toggle"]');
    const stopped = this.#reasons.has('manual') || this.#reasons.has('focus');
    if (toggle) {
      toggle.hidden = this.#reasons.has('reduced-motion');
      toggle.setAttribute('aria-label', stopped ? this.dataset.playLabel : this.dataset.pauseLabel);
      toggle.querySelector('[data-play-icon]').hidden = !stopped;
      toggle.querySelector('[data-pause-icon]').hidden = stopped;
    }
    if (!this.isConnected || this.dataset.autoplay !== 'true' || this.#slides.length < 2 || this.#busy || this.#reasons.size) return;
    let next = (this.#index + 1) % this.#slides.length;
    while (next !== this.#index && this.#failed.has(next)) next = (next + 1) % this.#slides.length;
    if (next === this.#index) return;
    const interval = Math.max(5000, Number(this.dataset.interval) || 6000);
    const progress = this.querySelector('[data-progress]');
    this.#progress = progress?.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration: interval, fill: 'forwards' });
    this.#timer = setTimeout(() => { void this.#select(next); }, interval);
  }
}

if (!customElements.get('preimum-hero-codex')) {
  customElements.define('preimum-hero-codex', PreimumHeroCodex);
}
